/**
 * AddUserModal - Quick "Add New User" popup for managers / admins / super admins.
 *
 * Lets a privileged user onboard a tech or individual (someone who can then be
 * assigned to a work order or inspection) without leaving whatever page they're
 * on. Collects the basics — name, email, phone, roles, and primary/other
 * businesses — and generates an easy, human-friendly temporary password.
 *
 * The role picker is the shared <RoleSelector>, the same one Admin → Users
 * renders, so a user onboarded from the header ends up with the same multi-role
 * assignment and the same users.role label as one created from the Admin page.
 *
 * On submit it POSTs to /users (which also emails the new user their temp
 * password + a sign-in link) and then shows a success panel that restates the
 * temp password for the creator to copy/share, since the account requires a
 * password change on first login.
 *
 * The CSR / JSR / Other roles are the exception: those are people you assign to
 * a job rather than platform accounts, so a first and last name is enough.
 * Leaving the email blank skips the password and the welcome email entirely —
 * email is the login identifier, so there'd be nothing to sign in with.
 *
 * @example
 * <AddUserModal isOpen={open} onClose={() => setOpen(false)} onCreated={refresh} />
 */
import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../Modal/Modal';
import { Button } from '../../atoms/Button';
import { Input } from '../../atoms/Input';
import { Alert } from '../../atoms/Alert';
import { Spinner } from '../../atoms/Spinner';
import { RoleSelector } from '../../molecules/RoleSelector';
import { SearchableSelect } from '../../molecules/SearchableSelect';
import { SearchableMultiSelect } from '../../molecules/SearchableMultiSelect';
import { TempPasswordDisplay } from '../../molecules/TempPasswordDisplay';
import { dedupeAndSort } from '../../../utils/dedupeAndSort';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { generateTempPassword, isValidEmail } from '../../../utils/tempPassword';
import {
	rolesAllowMissingCredentials,
	primaryRoleLabel,
	DEFAULT_ASSIGNED_ROLES,
} from '../../../utils/constants';
import {
	userService,
	type CompanyOption,
	type CreateUserResult,
} from '../../../services/userService';

/** Id linking the create-user <form> to the submit button pinned in the modal
 *  footer (the button renders outside the form element, so it needs form="…"). */
const ADD_USER_FORM_ID = 'add-user-form';

export interface AddUserModalProps {
	/** Whether the modal is visible. */
	isOpen: boolean;
	/** Called when the modal should close (X, Escape, backdrop, Done). */
	onClose: () => void;
	/** Optional callback fired after a user is successfully created. */
	onCreated?: (result: CreateUserResult) => void;
}

/** The editable form fields. */
interface FormState {
	firstName: string;
	lastName: string;
	/** Business name, for subcontractors only (field shows once that role is ticked). */
	subcontractorCompany: string;
	email: string;
	phone: string;
	/** Role slugs assigned via the user_roles junction table. Multi-select, the
	 *  same as the Admin page — a user is rarely only one thing. */
	roles: string[];
	primaryCompanyId: string;
	additionalCompanyIds: number[];
	tempPassword: string;
}

const EMPTY_FORM: FormState = {
	firstName: '',
	lastName: '',
	subcontractorCompany: '',
	email: '',
	phone: '',
	roles: [...DEFAULT_ASSIGNED_ROLES],
	primaryCompanyId: '',
	additionalCompanyIds: [],
	tempPassword: '',
};

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onCreated }) => {
	/* Form + option state */
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [availableRoles, setAvailableRoles] = useState<string[]>([]);
	const [companies, setCompanies] = useState<CompanyOption[]>([]);
	const [optionsLoading, setOptionsLoading] = useState(false);

	/* Submission state */
	const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	/* Success state — once set, the modal shows the confirmation panel. */
	const [created, setCreated] = useState<CreateUserResult | null>(null);

	/**
	 * Reset everything to a fresh form. Used when opening, and by "Add another"
	 * on the success panel.
	 */
	const resetForm = useCallback(() => {
		setForm({ ...EMPTY_FORM, roles: [...DEFAULT_ASSIGNED_ROLES], tempPassword: generateTempPassword() });
		setFieldErrors({});
		setSubmitError(null);
		setCreated(null);
	}, []);

	/* When the modal opens, reset the form and load the role + company options. */
	useEffect(() => {
		if (!isOpen) return;

		resetForm();
		let cancelled = false;
		setOptionsLoading(true);

		Promise.all([userService.getAssignableRoles(), userService.listCompanies()])
			.then(([assignableRoles, companyOptions]) => {
				if (cancelled) return;
				setAvailableRoles(assignableRoles);
				setCompanies(companyOptions);
				/* Keep only the defaults this user is actually allowed to assign —
				   a pre-ticked role the server would reject is worse than none. */
				setForm((prev) => ({
					...prev,
					roles: prev.roles.filter((role) => assignableRoles.includes(role)),
				}));
			})
			.catch(() => {
				if (!cancelled) setSubmitError('Could not load roles or businesses. Please try again.');
			})
			.finally(() => {
				if (!cancelled) setOptionsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [isOpen, resetForm]);

	/** Update a single field and clear its inline error. */
	const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
		setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
	};

	/** Set the "other businesses" selection. The picker works in strings (its
	 *	options are `{ value, label }`); the payload wants numeric company ids. */
	const setAdditionalCompanies = (next: string[]) => {
		setForm((prev) => ({ ...prev, additionalCompanyIds: next.map(Number) }));
	};

	/** Validate the required fields, returning true when the form is submittable. */
	const validate = (): boolean => {
		const errors: Partial<Record<keyof FormState, string>> = {};
		if (!form.firstName.trim()) errors.firstName = 'First name is required.';
		if (!form.lastName.trim()) errors.lastName = 'Last name is required.';
		/* Email is the login identifier, so it's required for every role that is
		   an account — but optional for the service-rep roles, which exist purely
		   as assignable people. Mirrors the rule UserController enforces. */
		if (!form.email.trim()) {
			if (!rolesAllowMissingCredentials(form.roles)) errors.email = 'Email is required.';
		} else if (!isValidEmail(form.email)) {
			errors.email = 'Enter a valid email address.';
		}
		if (form.roles.length === 0) errors.roles = 'Select at least one role.';
		setFieldErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitError(null);
		if (!validate()) return;

		setSubmitting(true);
		try {
			const email = form.email.trim();
			const result = await userService.create({
				firstName: form.firstName.trim(),
				lastName: form.lastName.trim(),
				/* Only a subcontractor carries a business name. */
				subcontractorCompany: form.roles.includes('subcontractor')
					? (form.subcontractorCompany.trim() || null)
					: null,
				/* null, not '' — a blank email means "no sign-in", and null is what
				   keeps the unique index treating these users as distinct rows. */
				email: email || null,
				phone: form.phone.trim() || undefined,
				/* The users.role label column is an ENUM with no slot for
				   'super_admin' or 'company_admin', so derive it from the checked
				   roles rather than posting the raw selection. */
				role: primaryRoleLabel(form.roles),
				roles: form.roles,
				/* No address, no password: it would be a secret nobody could use. */
				password: email ? form.tempPassword : undefined,
				companyId: form.primaryCompanyId ? Number(form.primaryCompanyId) : undefined,
				additionalCompanyIds: form.additionalCompanyIds,
			});
			setCreated(result);
			onCreated?.(result);
		} catch (err) {
			/* Surface backend validation / authorization messages when present —
			   apiErrorMessage picks the field-level one, which names the actual
			   problem, over the generic envelope message. */
			setSubmitError(apiErrorMessage(err, 'Could not create the user. Please try again.'));
		} finally {
			setSubmitting(false);
		}
	};

	/* Companies available as "other businesses" — exclude the chosen primary. */
	const otherBusinessOptions = companies.filter(
		(c) => String(c.id) !== form.primaryCompanyId,
	);

	/* ------------------------------------------------------------------ *
	 * Success panel
	 * ------------------------------------------------------------------ */
	const renderSuccess = (result: CreateUserResult) => (
		<div className="space-y-16px">
			<Alert variant="success">
				<span className="font-semibold">
					{result.user.firstName} {result.user.lastName}
				</span>{' '}
				{result.user.email ? (
					<>
						was added. We emailed sign-in instructions to{' '}
						<span className="font-semibold">{result.user.email}</span>.
					</>
				) : (
					<>
						was added. They have no email address, so they can't sign in — you can
						still assign them to work orders and inspections.
					</>
				)}
			</Alert>

			{result.user.email && result.tempPassword && (
				<TempPasswordDisplay
					value={result.tempPassword}
					label="Temporary password"
					helperText="Share this with the user if needed — they'll be required to set their own password the first time they sign in."
					className="add-user-modal__credentials rounded-8px border border-brand-border bg-brand-surface p-16px"
				/>
			)}

		</div>
	);

	/* Success-state actions — rendered in the Modal's pinned footer */
	const renderSuccessActions = () => (
		<div className="add-user-modal__actions flex flex-col-reverse tablet:flex-row tablet:items-center tablet:justify-end gap-12px">
			<Button variant="ghost" onClick={resetForm} className="w-full tablet:w-auto">
				Add another
			</Button>
			<Button variant="primary" onClick={onClose} className="w-full tablet:w-auto">
				Done
			</Button>
		</div>
	);

	/* ------------------------------------------------------------------ *
	 * Create form
	 * ------------------------------------------------------------------ */
	const renderForm = () => {
		/* CSR / JSR / Other need nothing but a name, so the email loses its
		   required marker and the temp-password block disappears until an
		   address is typed. Any other role is an account and needs both. */
		const credentialsOptional = rolesAllowMissingCredentials(form.roles);

		return (
		<form id={ADD_USER_FORM_ID} onSubmit={handleSubmit} className="add-user-modal__form space-y-16px">
			{submitError && <Alert variant="error">{submitError}</Alert>}

			{/* Name — two columns on wider screens */}
			<div className="grid grid-cols-1 tablet:grid-cols-2 gap-12px">
				<Input
					label="First name"
					required
					value={form.firstName}
					onChange={(e) => setField('firstName', e.target.value)}
					error={fieldErrors.firstName}
					autoFocus
				/>
				<Input
					label="Last name"
					required
					value={form.lastName}
					onChange={(e) => setField('lastName', e.target.value)}
					error={fieldErrors.lastName}
				/>
			</div>

			<Input
				label="Email"
				type="email"
				required={!credentialsOptional}
				value={form.email}
				onChange={(e) => setField('email', e.target.value)}
				error={fieldErrors.email}
				/* Spell out what blank costs — "optional email" shouldn't leave the
				   creator guessing that it also means "cannot sign in". */
				helperText={
					credentialsOptional && !form.email.trim()
						? "Optional. Without one they can't sign in, but can still be assigned to jobs."
						: "They'll use this to sign in."
				}
			/>

			<Input
				label="Phone"
				type="tel"
				value={form.phone}
				onChange={(e) => setField('phone', e.target.value)}
				error={fieldErrors.phone}
			/>

			{/* Role assignment — the same shared picker the Admin page uses, so a
			   user created from the header gets the same multi-role treatment,
			   the same descriptions, and the same wording as one created from
			   Admin → Users. */}
			<RoleSelector
				options={availableRoles}
				value={form.roles}
				onChange={(roles) => setField('roles', roles)}
				required
				loading={optionsLoading}
				error={fieldErrors.roles}
				maxHeight={220}
				className="add-user-modal__roles"
				dataRef="organism-add-user-modal-roles"
				optionDataRef="organism-add-user-modal-role-option"
			/>

			{/* Subcontractor company — same field as Admin → Users, shown once
			   the Subcontractor role is ticked so their business name is kept
			   apart from their own name. */}
			{form.roles.includes('subcontractor') && (
				<Input
					label="Subcontractor company"
					className="add-user-modal__subcontractor-company"
					value={form.subcontractorCompany}
					onChange={(e) => setField('subcontractorCompany', e.target.value)}
					placeholder="e.g. Cyclone Window Cleaning"
					helperText="The business they work for. Shown next to their name wherever people are picked."
				/>
			)}

			{/* Primary business — searchable, matching the "Other businesses"
			   picker below it and the Primary Company field on Admin → Users.
			   SearchableSelect dedupes and alphabetizes the options itself. */}
			<div className="w-full">
				<label htmlFor="add-user-company" className="block text-14px font-sans font-medium text-gray-700 mb-4px">
					Primary business
				</label>
				<SearchableSelect
					id="add-user-company"
					options={companies.map((company) => ({ value: String(company.id), label: company.name }))}
					value={form.primaryCompanyId}
					onChange={(value) => setField('primaryCompanyId', value)}
					disabled={optionsLoading}
					placeholder={optionsLoading ? 'Loading…' : 'Select a business'}
				/>
			</div>

			{/* Other businesses — the same search-and-add picker Admin → Users
			   uses for its "Additional Companies" field, so the two create-user
			   forms ask the question the same way. It also scales past the
			   scrolling checkbox box it replaces, which turned a long customer
			   list into a hunt for a name you could have typed. */}
			{otherBusinessOptions.length > 0 && (
				<div className="w-full">
					<span className="block text-14px font-sans font-medium text-gray-700 mb-4px">
						Other businesses
					</span>
					<SearchableMultiSelect
						id="add-user-other-businesses"
						options={dedupeAndSort(otherBusinessOptions.map((company) => ({ value: String(company.id), label: company.name })))}
						selectedValues={form.additionalCompanyIds.map(String)}
						onChange={setAdditionalCompanies}
						placeholder="Search businesses..."
						emptyText="No other businesses yet — search above to add one."
						allSelectedText="Every other business is already on this user."
						className="add-user-modal__business"
						dataRef="organism-add-user-modal-business"
					/>
				</div>
			)}

			{/* Temp password — only when the account can actually be signed into.
			   A name-only person has no login identifier, so there is no password
			   to generate, show, or email. */}
			{form.email.trim() && (
			<div className="w-full">
				<span className="block text-14px font-sans font-medium text-gray-700 mb-4px">
					Temporary password
				</span>
				<div className="flex items-center gap-8px">
					<code className="flex-1 rounded-6px border border-gray-300 bg-brand-surface px-12px py-10px text-14px font-bold text-brand-primary break-all min-h-44px flex items-center">
						{form.tempPassword}
					</code>
					<Button
						variant="secondary"
						size="sm"
						onClick={() => setField('tempPassword', generateTempPassword())}
					>
						Regenerate
					</Button>
				</div>
				<p className="mt-4px text-14px font-sans text-gray-500">
					Emailed to the user. They must change it on first sign-in.
				</p>
			</div>
			)}

		</form>
		);
	};

	/* Create-form actions — rendered in the Modal's pinned footer so they stay
	   reachable on short screens instead of scrolling off the panel. The submit
	   button targets the form by id since it lives outside the <form>. */
	const renderFormActions = () => (
		<div className="add-user-modal__actions flex flex-col-reverse tablet:flex-row tablet:items-center tablet:justify-end gap-12px">
			<Button variant="ghost" onClick={onClose} disabled={submitting} className="w-full tablet:w-auto">
				Cancel
			</Button>
			<Button type="submit" form={ADD_USER_FORM_ID} variant="primary" isLoading={submitting} disabled={optionsLoading} className="w-full tablet:w-auto">
				Create user
			</Button>
		</div>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={created ? 'User created' : 'Add new user'}
			size="md"
			className="add-user-modal"
			dataRef="organism-add-user-modal"
			/* Actions pinned below the scroll area — hidden entirely while the
			   company/role options are still loading, since there is nothing to
			   act on yet. */
			footer={optionsLoading && !created ? undefined : created ? renderSuccessActions() : renderFormActions()}
		>
			{optionsLoading && !created ? (
				<div className="flex items-center justify-center py-32px">
					<Spinner size="md" />
				</div>
			) : created ? (
				renderSuccess(created)
			) : (
				renderForm()
			)}
		</Modal>
	);
};

export default AddUserModal;
